import { Injectable } from '@angular/core'
import { Action, NgxsOnInit, NgxsSimpleChange, Select, Selector, State, StateContext, Store } from '@ngxs/store'
import { DateTime } from 'luxon'
import { Observable } from 'rxjs'
import { CountDraft, ToolInventory } from 'src/app/domain/tools'
import { StorageService } from 'src/app/services/storage.service'
import { ToolService } from 'src/app/services/tool.service'
import { User } from 'src/app/state-man/models/user.model'
import { UserState } from 'src/app/state-man/state/user.state'
import { ToolActions, ToolPatch } from '../actions/tool.actions'
import { stateContentChanged } from './state-content-changed'

export interface ToolStateModel {
  inventory: ToolInventory | null
  /** Count edits saved on the device, by count id, until Submit clears them. */
  drafts: { [countId: number]: CountDraft }
  is_refreshing: boolean
  last_update: string
}

const STORAGE_KEY = 'tools'
const stamp = () => DateTime.now().toFormat('M/d/yyyy, h:mm a')

/** Newer rows replace the inventory's copies by id; rows it has not seen go first, so a new handoff is at the top. */
function merge<T extends { readonly id: number }>(current: readonly T[], rows: readonly T[] | undefined): readonly T[] {
  if (!rows?.length) return current
  const fresh = new Map(rows.map(r => [r.id, r]))
  const kept = current.map(c => fresh.get(c.id) ?? c)
  const seen = new Set(current.map(c => c.id))
  return [...rows.filter(r => !seen.has(r.id)), ...kept]
}

@State<ToolStateModel>({
  name: 'tools',
  defaults: { inventory: null, drafts: {}, is_refreshing: true, last_update: '' },
})
@Injectable()
export class ToolState implements NgxsOnInit {
  @Select(UserState.getUser) user$: Observable<User>

  constructor(private toolService: ToolService, private store: Store, private storageService: StorageService) {}

  ngxsOnInit() {
    this.user$.subscribe((user: User) => {
      if (user.is_logged_in) {
        this.storageService.events.subscribe(async () => {
          if (this.storageService.is_initialized) await this.reloadStateFromStorage()
        })
      }
    })
  }

  async ngxsOnChanges(change: NgxsSimpleChange) {
    if (this.storageService.is_initialized && stateContentChanged(change)) {
      await this.storageService.set(STORAGE_KEY, JSON.stringify(change.currentValue))
    }
  }

  async reloadStateFromStorage() {
    const json: string = await this.storageService.get(STORAGE_KEY)
    const saved: ToolStateModel | null = json ? JSON.parse(json) : null
    if (saved) this.store.dispatch(new ToolActions.Init(saved))
    else this.store.dispatch(new ToolActions.Refresh())
  }

  @Selector()
  static getInventory(state: ToolStateModel): ToolInventory | null {
    return state.inventory
  }

  @Selector()
  static getDrafts(state: ToolStateModel): ToolStateModel['drafts'] {
    return state.drafts
  }

  @Selector()
  static getLastUpdate(state: ToolStateModel): string {
    return state.last_update
  }

  @Selector()
  static getIsRefreshing(state: ToolStateModel): boolean {
    return state.is_refreshing
  }

  @Action(ToolActions.Refresh)
  async refresh({ patchState }: StateContext<ToolStateModel>) {
    patchState({ is_refreshing: true })
    const inventory = await this.toolService.getMine()
    if (inventory) patchState({ inventory, is_refreshing: false, last_update: stamp() })
    else patchState({ is_refreshing: false })
  }

  @Action(ToolActions.Init)
  init({ setState, getState }: StateContext<ToolStateModel>, { payload }: ToolActions.Init) {
    setState({ ...payload, drafts: payload.drafts ?? {}, is_refreshing: getState().is_refreshing })
    this.store.dispatch(new ToolActions.Refresh())
  }

  @Action(ToolActions.Set)
  set({ patchState }: StateContext<ToolStateModel>, { payload }: ToolActions.Set) {
    patchState({ inventory: payload, is_refreshing: false, last_update: stamp() })
  }

  @Action(ToolActions.Patch)
  patch({ getState, patchState }: StateContext<ToolStateModel>, { payload }: ToolActions.Patch) {
    const inventory = getState().inventory
    if (!inventory) return
    patchState({ inventory: applyPatch(inventory, payload), last_update: stamp() })
  }

  @Action(ToolActions.SetDraft)
  setDraft({ getState, patchState }: StateContext<ToolStateModel>, { payload }: ToolActions.SetDraft) {
    patchState({ drafts: { ...getState().drafts, [payload.countId]: payload.draft } })
  }

  @Action(ToolActions.ClearDraft)
  clearDraft({ getState, patchState }: StateContext<ToolStateModel>, { payload }: ToolActions.ClearDraft) {
    const { [payload.countId]: _gone, ...drafts } = getState().drafts
    patchState({ drafts })
  }
}

export function applyPatch(inventory: ToolInventory, p: ToolPatch): ToolInventory {
  return {
    ...inventory,
    counts: merge(inventory.counts, p.counts),
    handoffs: merge(inventory.handoffs, p.handoffs),
    reports: merge(inventory.reports, p.reports),
  }
}
